import { useQuery } from '@tanstack/react-query';
import { dashboardClient, superadminStatisticsClient } from 'api';
import type { Statistics } from 'api/models/api';
import type { GetDataForDashboardQueryResult } from 'api/models/mainservice';
import type { ApiError } from 'common/models';
import { DASHBOARD_STALE_TIME } from 'common/queries';

import type {
  CompanyTrendStatRaw,
  DashboardOverview,
  Granularity,
  TopCompaniesOrder,
  TopCompanyStatRaw
} from './models';

export const useGetMainStatisticsQuery = () => {
  return useQuery<GetDataForDashboardQueryResult, ApiError>({
    queryKey: ['mainStatistics'],
    queryFn: dashboardClient.getMainStatistics,
    staleTime: DASHBOARD_STALE_TIME
  });
};

export const useGetCompanyStatisticsQuery = () => {
  return useQuery<Statistics, ApiError>({
    queryKey: ['dashboardCompanyStatistics'],
    queryFn: dashboardClient.getCompanyStatistics,
    staleTime: DASHBOARD_STALE_TIME
  });
};

export const useDashboardOverviewQuery = (from: string, to: string, granularity: Granularity) => {
  return useQuery<DashboardOverview, ApiError>({
    queryKey: ['superadminOverview', from, to, granularity],
    queryFn: () => superadminStatisticsClient.getOverview(from, to, granularity),
    staleTime: DASHBOARD_STALE_TIME
  });
};

export const useTopCompaniesQuery = (
  from: string,
  to: string,
  limit = 10,
  orderBy: TopCompaniesOrder = 'coreActivity'
) => {
  return useQuery<TopCompanyStatRaw[], ApiError>({
    queryKey: ['superadminTopCompanies', from, to, limit, orderBy],
    queryFn: () => superadminStatisticsClient.getTopCompanies(from, to, limit, orderBy),
    staleTime: DASHBOARD_STALE_TIME
  });
};

export const useCompanyTrendsQuery = (from: string, to: string) => {
  return useQuery<CompanyTrendStatRaw[], ApiError>({
    queryKey: ['superadminCompanyTrends', from, to],
    queryFn: () => superadminStatisticsClient.getCompanyTrends(from, to),
    staleTime: DASHBOARD_STALE_TIME
  });
};
